import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  InsightsCard,
  InsightsCardRequeryRequest,
  InsightsChatRequest,
  InsightsChatResponse,
  InsightsConversationDetail,
  InsightsConversationSummary,
  InsightsDrillRequest,
  InsightsDrillResult,
  InsightsFeedback,
  InsightsMemberOption,
  InsightsSavedQuestion,
  InsightsStarters,
  InsightsStudioStatus,
  InsightsUsageReport,
  MemberLensView,
  PaginatedResult,
} from '@catalyst-psa/shared';

import { api, apiFetchRaw } from '../../api/client.js';

const insightsKeys = {
  status: ['insights-studio', 'status'] as const,
  starters: ['insights-studio', 'starters'] as const,
  savedQuestions: ['insights-studio', 'saved-questions'] as const,
  usage: (days: number) => ['insights-studio', 'admin-usage', days] as const,
  conversations: ['insights-studio', 'conversations'] as const,
  conversation: (id: string) => ['insights-studio', 'conversations', id] as const,
  memberOptions: ['insights-studio', 'member-options'] as const,
  memberLens: (memberId: string, months: number) =>
    ['insights-studio', 'member-lens', memberId, months] as const,
};

/** Whether Insights Studio is configured server-side and granted to this member. */
export function useInsightsStudioStatus(): UseQueryResult<InsightsStudioStatus> {
  return useQuery({
    queryKey: insightsKeys.status,
    queryFn: () => api.get<InsightsStudioStatus>('/insights-studio/status'),
    staleTime: 60_000,
  });
}

/** Sends ONE new question; the server owns the history and returns the conversation id. */
export function sendInsightsChat(request: InsightsChatRequest): Promise<InsightsChatResponse> {
  return api.post<InsightsChatResponse>('/insights-studio/chat', request);
}

/** Re-runs one card's query with an adjusted spec — pure engine work, no model call. */
export function requeryInsightsCard(request: InsightsCardRequeryRequest): Promise<InsightsCard> {
  return api.post<InsightsCard>('/insights-studio/cards/requery', request);
}

/** Starter question chips personalized from the member's own recent work. */
export function useInsightsStarters(enabled: boolean): UseQueryResult<InsightsStarters> {
  return useQuery({
    queryKey: insightsKeys.starters,
    queryFn: () => api.get<InsightsStarters>('/insights-studio/starters'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Thumbs up/down on an assistant answer (null clears the rating). */
export function setInsightsFeedback(
  messageId: string,
  feedback: InsightsFeedback | null,
): Promise<void> {
  return api.post<void>(`/insights-studio/messages/${messageId}/feedback`, { feedback });
}

export function useSavedInsightsQuestions(enabled: boolean): UseQueryResult<InsightsSavedQuestion[]> {
  return useQuery({
    queryKey: insightsKeys.savedQuestions,
    queryFn: () => api.get<InsightsSavedQuestion[]>('/insights-studio/saved-questions'),
    enabled,
    staleTime: 60_000,
  });
}

export function useSaveInsightsQuestion(): UseMutationResult<InsightsSavedQuestion, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (question: string) =>
      api.post<InsightsSavedQuestion>('/insights-studio/saved-questions', { question }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: insightsKeys.savedQuestions }),
  });
}

export function useDeleteSavedInsightsQuestion(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/insights-studio/saved-questions/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: insightsKeys.savedQuestions }),
  });
}

/** ADMIN: Insights Studio usage observability. */
export function useInsightsUsage(days: number, enabled: boolean): UseQueryResult<InsightsUsageReport> {
  return useQuery({
    queryKey: insightsKeys.usage(days),
    queryFn: () => api.get<InsightsUsageReport>(`/insights-studio/admin/usage?days=${days}`),
    enabled,
    staleTime: 60_000,
  });
}

export interface InsightsStreamHandlers {
  onToken: (text: string) => void;
  onToolStart: (label: string) => void;
  onCard: (card: InsightsCard) => void;
}

/**
 * Streaming variant over server-sent events: narrative tokens type out and each chart
 * card renders the moment it's ready. Resolves with the final response from `done`;
 * rejects on `error` events or an aborted signal.
 */
export async function sendInsightsChatStream(
  request: InsightsChatRequest,
  handlers: InsightsStreamHandlers,
  signal: AbortSignal,
): Promise<InsightsChatResponse> {
  const response = await apiFetchRaw('/insights-studio/chat/stream', {
    method: 'POST',
    body: request,
    headers: { Accept: 'text/event-stream' },
    signal,
  });
  const reader = response.body?.getReader();
  if (!reader) throw new Error('The stream had no body.');

  const decoder = new TextDecoder();
  let buffer = '';
  let done: InsightsChatResponse | null = null;

  const handleEvent = (raw: string): void => {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (dataLines.length === 0) return;
    const data = JSON.parse(dataLines.join('')) as unknown;
    if (event === 'token') handlers.onToken((data as { text: string }).text);
    else if (event === 'tool_start') handlers.onToolStart((data as { label: string }).label);
    else if (event === 'card') handlers.onCard(data as InsightsCard);
    else if (event === 'done') done = data as InsightsChatResponse;
    else if (event === 'error') throw new Error((data as { message?: string }).message ?? 'Stream failed.');
  };

  for (;;) {
    const { done: finished, value } = await reader.read();
    if (finished) break;
    buffer += decoder.decode(value, { stream: true });
    let separator: number;
    while ((separator = buffer.indexOf('\n\n')) >= 0) {
      const raw = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      handleEvent(raw);
    }
  }

  if (!done) throw new Error('The stream ended before an answer was completed.');
  return done;
}

/** The member's own conversations, newest activity first. */
export function useInsightsConversations(): UseQueryResult<
  PaginatedResult<InsightsConversationSummary>
> {
  return useQuery({
    queryKey: insightsKeys.conversations,
    queryFn: () =>
      api.get<PaginatedResult<InsightsConversationSummary>>(
        '/insights-studio/conversations?page=1&pageSize=100',
      ),
    staleTime: 30_000,
  });
}

/** A full stored conversation (turns + replayable cards). */
export function useInsightsConversation(
  id: string | null,
): UseQueryResult<InsightsConversationDetail> {
  return useQuery({
    queryKey: insightsKeys.conversation(id ?? ''),
    queryFn: () => api.get<InsightsConversationDetail>(`/insights-studio/conversations/${id}`),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useRenameInsightsConversation(): UseMutationResult<
  InsightsConversationSummary,
  unknown,
  { id: string; title: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }) =>
      api.patch<InsightsConversationSummary>(`/insights-studio/conversations/${id}`, { title }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: insightsKeys.conversations });
    },
  });
}

export function useDeleteInsightsConversation(): UseMutationResult<unknown, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/insights-studio/conversations/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: insightsKeys.conversations });
    },
  });
}

export { insightsKeys };

/** Fetches the underlying records behind a card/segment (each domain drills its own list). */
export function drillInsights(request: InsightsDrillRequest): Promise<InsightsDrillResult> {
  return api.post<InsightsDrillResult>('/insights-studio/drill', request);
}

/** Member options for the lens picker (Insights-Studio-gated endpoint). */
export function useInsightsMemberOptions(enabled: boolean): UseQueryResult<InsightsMemberOption[]> {
  return useQuery({
    queryKey: insightsKeys.memberOptions,
    queryFn: () => api.get<InsightsMemberOption[]>('/insights-studio/member-options'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** The "what has this person been doing" view for one member. */
export function useMemberLens(
  memberId: string | null,
  months: number,
): UseQueryResult<MemberLensView> {
  return useQuery({
    queryKey: insightsKeys.memberLens(memberId ?? '', months),
    queryFn: () =>
      api.get<MemberLensView>(`/insights-studio/member-lens/${memberId}?months=${months}`),
    enabled: Boolean(memberId),
    staleTime: 60_000,
  });
}
